import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useMemo,
} from "react";
import { menuInfo } from "@shared/type/CommonType";
import { global } from "@shared/libs/Global";
import usePanelSizes from "@shared/hooks/usePanelSizes";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import SearchPanel from "@widgets/SearchPanel";
import SnResizable from "@shared/components/SnResizable";
import SnGrid from "@shared/components/GridV2/SnGrid";
import SnTab from "@shared/components/SnTab";
import SnForm, { ValueChangedEvent } from "@widgets/SnForm";

interface Props {
  Menuprops: menuInfo;
}

interface RowData {
  KeyField?: string;
  [key: string]: any;
}

const SN_PRD_BAS_REG_036 = forwardRef(({ Menuprops }: Props, ref) => {
  // ref
  const searchRef = useRef<any>(null);
  const sharedFormRef1 = useRef<any>();
  const initJsOpRef = useRef<any>(null);
  const popupRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
  });
  const [columns, setColums] = useState<any>({
    grid1: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
  });
  const formRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    freeform1: React.createRef(),
  });
  const [formControls, setFormControls] = useState<any>({
    freeform1: [],
  });
  const [formDatas, setFormDatas] = useState<any>({
    freeform1: null,
  });
  // search
  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);

  // grid, form

  // util
  const [focusedRowData, setFocusedRowData] = useState<any>();
  const [gridFocus, setGridFocus] = useState(false);
  const [popupComponent, setPopupComponent] = useState<React.ReactNode>(null);
  const [selectedTab, setSelectedTab] = useState<string>("1");

  // golbal variable
  const { userInfo } = global();

  //#region grid 길이 관련
  const cd_menu = Menuprops.cd_menu || "SN_PRD_BAS_REG_036";
  const defaultSize = {
    panel1width: "45%",
    panel2width: "54%",
  };
  const [sizes, onResizeStop] = usePanelSizes(cd_menu, defaultSize);
  //#endregion

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormData(rtn.formData);
        setFormControlData(rtn.formControlData);
        setColums({
          grid1: rtn.grid.grid1,
        });
        setFormControls({
          freeform1: rtn.form.freeform1,
        });

        const rtn2 = await fnGetDataSet({
          id: "sn_prd_bas_reg_036_02",
          params: {},
        });
        const data = rtn2?.[0]?.[0]?.js_op ?? null;
        initJsOpRef.current = data || "";
      } catch (error: any) {
        await showDialog("error", error);
      }
    };
    fetchform();
  }, []);

  useEffect(() => {
    gridRefs.current["grid1"].current.selectAndFocusRow(0);
    setGridFocus(false);
  }, [gridFocus]);

  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  const onSearch = async () => {
    const modifiedRows1 = gridRefs.current["grid1"].current.getChange();
    if (modifiedRows1.modifiedRowcount > 0) {
      const confirm = await showDialog(
        "confirm",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        await onSave();
      }
    }
    try {
      const param = {
        id: "sn_prd_bas_reg_036_01",
        params: searchRef.current.getFormdata(),
      };
      const data: any = await fnGetDataSet(param);
      setFormDatas({
        freeform1: null,
      });
      setDataSources({
        grid1: data[0],
      });
      setGridFocus(true);
      gridRefs.current["grid1"].current.resetUpdate();
    } catch (error: any) {}
  };

  const onInsert = async () => {
    const focusedRowNodeH = gridRefs.current["grid1"].current.focusedRowNode();
    const newRow = {
      cd_firm: userInfo.cd_firm,
      yn_use: "N",
      js_op: initJsOpRef.current || "",
    };
    gridRefs.current["grid1"].current.addRow(newRow);
  };

  const onDelete = async () => {
    const focusedRowNode = gridRefs.current["grid1"]?.current.focusedRowNode();
    let rowIndex: any = 0;

    if (focusedRowNode) {
      rowIndex = focusedRowNode.rowIndex;
    }

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");

    if (!confirm) {
      return; // 사용자가 아니오를 선택하면 함수 종료
    }

    gridRefs.current["grid1"].current?.deleteSelectedRows();

    const realDeletedRows =
      gridRefs.current["grid1"].current.getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      // 실제로 삭제된 행이 없으면 변경 사항을 적용하고 반환합니다.
      // gridRefs.current["grid1"].current.resetUpdate();
      // gridRefs.current["grid2"].current.resetUpdate();

      if (gridRefs.current["grid1"].current?.getDisplayedRowCount() > 0) {
        const newRowIndex = rowIndex === 0 ? 0 : rowIndex - 1;
        gridRefs.current["grid1"].current?.selectAndFocusRow(newRowIndex);
      } else {
        setFormDatas({
          freeform1: null,
        });
      }
      return;
    }

    if (gridRefs.current["grid1"].current?.getDisplayedRowCount() > 0) {
      const newRowIndex = rowIndex === 0 ? 0 : rowIndex - 1;
      gridRefs.current["grid1"].current?.selectAndFocusRow(newRowIndex);
    } else {
      setFormDatas({
        freeform1: null,
      });
    }
    await onSave();
  };

  const onSave = async () => {
    try {
      const requiredValuesCheckH =
        gridRefs.current["grid1"].current.requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return;
      }

      gridRefs.current["grid1"].current.stopEditing();
      const gridGetChanges = gridRefs.current["grid1"].current.getChange();
      if (gridGetChanges.modifiedRowcount == 0) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return;
      }

      const saveData = {
        prd_opfg: gridGetChanges,
      };
      await fnSave("SN_PRD_BAS_REG_036_SAVE_01", saveData);
      gridRefs.current["grid1"].current.resetUpdate();
      formRefs.current["freeform1"].current.resetUpdate();

      setTimeout(() => {
        gridRefs.current["grid1"].current.clearFocus();
        formRefs.current["freeform1"].current.deleteForm();
      }, 0);
    } catch (error: any) {}
  };

  const onValueChanged = async (event: ValueChangedEvent) => {
    const focusedRowNodeH = gridRefs.current["grid1"].current.focusedRowNode();
    const { colId, newValue } = event;
    switch (colId) {
      case "fg_op":
        focusedRowNodeH.setDataValue("fg_op", newValue, "noEvent");
        break;
      case "nm_op":
        focusedRowNodeH.setDataValue("nm_op", newValue, "noEvent");
        break;
      case "dc_op":
        focusedRowNodeH.setDataValue("dc_op", newValue, "noEvent");
        break;
      case "yn_use":
        focusedRowNodeH.setDataValue("yn_use", newValue, "noEvent");
        break;
      case "cd_popup":
        focusedRowNodeH.setDataValue("cd_popup", newValue, "noEvent");
        break;
    }
  };
  const onFocusedRowChanged = useCallback(async (rowData: any | null) => {
    setFormDatas({
      freeform1: rowData,
    });
    const cd_popup = rowData?.cd_popup || "";
    if (cd_popup) {
      try {
        const module = await import(`../WRK_REG_POPUP/${cd_popup}.tsx`);
        const PopupComponent = module.default;

        if (PopupComponent) {
          setPopupComponent(<PopupComponent code={cd_popup} ref={popupRef} />);
        } else {
          setPopupComponent(null);
        }
      } catch (error) {
        setPopupComponent(null);
      }
    } else {
      setPopupComponent(null);
    }
  }, []);
  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onFocusedRowChanged,
        },
      },
    }),
    [columns, dataSources]
  );

  const forms = useMemo(
    () => ({
      freeform1: {
        controls: formControls.freeform1,
        data: formDatas.freeform1,
        etcProps: {},
        events: {
          onValueChanged: onValueChanged,
        },
      },
    }),
    [formControls, formDatas]
  );
  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>
      <div className="w-full flex h-full min-h-0 space-x-1.5">
        <SnResizable
          direction={"width"}
          seq={1}
          cd_menu={cd_menu}
          defaultSize={defaultSize}
          size={sizes.panel1width}
          onResizeStop={onResizeStop}
          enable={{ right: true }}
          minWidth={130}
          minHeight={130}
        >
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{ rowSelection: "single", title: "공정구분" }}
            events={grids.grid1.events}
          />
        </SnResizable>
        <SnResizable
          direction={"width"}
          seq={2}
          cd_menu={cd_menu}
          defaultSize={defaultSize}
          size={sizes.panel2width}
          onResizeStop={onResizeStop}
          enable={{ right: true }}
          minWidth={130}
          minHeight={130}
        >
          <SnTab
            titles={["상세정보", "미리보기"]}
            width={"100%"}
            onTabChanged={setSelectedTab}
            body={[
              <SnForm
                ref={formRefs.current["freeform1"]}
                data={forms.freeform1.data}
                controls={forms.freeform1.controls}
                events={forms.freeform1.events}
              />,
              <div
                className="w-full h-full flex items-center justify-center"
                style={{
                  border: "1px solid rgb(186,191,199)",
                  backgroundColor: "#fafafa",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    overflow: "auto",
                    pointerEvents: "none",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flex: 1,
                      flexDirection: "column",
                      overflow: "visible",
                    }}
                  >
                    {selectedTab === "2" && popupComponent ? (
                      popupComponent
                    ) : (
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#777",
                          fontSize: "15px",
                        }}
                      >
                        팝업이 존재하지 않습니다.
                      </div>
                    )}
                  </div>
                </div>
              </div>,
            ]}
          />
        </SnResizable>
      </div>
    </div>
  );
});

export default SN_PRD_BAS_REG_036;
